import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import NavigateNextOutlinedIcon from '@mui/icons-material/NavigateNextOutlined';
import { useNavigate } from 'react-router-dom';
import dayjs from '@/config/dayjs';
import { PaperFrameStyled } from '@/components/common/paperFrameStyled';
import DateRange from '@/components/date/DateRange';
import AccountDetailsFlyout from '@/components/flyouts/AccountDetailsFlyout/AccountDetailsFlyout';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useCanNavigateTo } from '@/hooks/useCanNavigateTo';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import { OUTLINED_INPUT_HEIGHT } from '@/constants/dimensions';
import {
  OpportunityRuleIdEnum,
  OpportunityStatusEnum,
  useGetRuleOpportunitiesQuery,
  type OpportunityItem,
} from '@/store/insightsApi';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatAppDate } from '@/utils/date';
import type { AccountBase } from '@/models/AccountViewModel';
import type { AdvisorInvestor } from '@/store/usersApi';
import ClientDetailsFlyout from '@/components/flyouts/ClientDetailsFlyout/ClientDetailsFlyout';
import { useLazyGetAccountsQuery } from '@/store/accountsApi';
import { useControlledDates } from '@/hooks/useControlledDates';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { dashboardMobileWidthValue } from '@/constants';
import type { StandardWidgetProps } from '@/models/WidgetModels';
import { buildAdvisorClientDetailUrl } from '@/modules/advisor/utils/clientDetailRouting';
import { isEmbeddableWidgetBundle } from '@/integrations/embeddableWidgetPortalUtils';
import { paths } from '@/constants/paths';

type InsightRow = {
  id: string;
  clientName: string;
  investorId: number | null;
  amount: number;
  date: string | null;
  wealthAccountId: number | null;
  ruleId: OpportunityRuleIdEnum;
  item: OpportunityItem;
};

const ITEMS_PER_PAGE = 5;
const EXCLUDED_STATUS_IDS = new Set<OpportunityStatusEnum>([
  OpportunityStatusEnum.Closed,
  OpportunityStatusEnum.Converted,
]);

const RECENT_INSIGHT_RULE_IDS: OpportunityRuleIdEnum[] = [
  OpportunityRuleIdEnum.MoneyInMotionNewAccounts,
  OpportunityRuleIdEnum.MoneyInMotionLargeDeposits,
  OpportunityRuleIdEnum.Rollover,
  OpportunityRuleIdEnum.InvestableCash,
];

const RECENT_INSIGHT_RULE_IDS_SET = new Set(RECENT_INSIGHT_RULE_IDS);

const { startDate: initialWidgetStartDate, endDate: initialWidgetEndDate } =
  getDateRangeFromFilter(DataRangeFilterEnum.Trailing7Days);

type RecentInsightsWidgetProps = StandardWidgetProps;

export default function RecentInsightsWidget({
  startDate: propStartDate,
  endDate: propEndDate,
  isWidget = false,
  onDataLoaded,
  title,
}: RecentInsightsWidgetProps = {}) {
  const { t } = useLocalization();
  const canNavigateToClients = useCanNavigateTo(paths.advisor.clients.index);
  const navigate = useNavigate();
  const widgetTitle = title ?? t('recentInsights.title', 'Recent Insights');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { ref: widgetWidthRef, isBelow: isWidgetBelowNarrowDateField } =
    useWidgetWidthIsBelow(isWidget, dashboardMobileWidthValue);
  const widgetNarrowDateField = isWidget && isWidgetBelowNarrowDateField;
  const authLoaded = useAuthIsLoaded();
  const normalizeDisplayDate = useCallback((value?: string) => {
    if (!value) return undefined;
    const parsedFromDisplay = dayjs(value, 'MM/DD/YYYY');
    if (parsedFromDisplay.isValid()) {
      return parsedFromDisplay.format('MM/DD/YYYY');
    }
    const parsedFromIso = dayjs(value);
    return parsedFromIso.isValid()
      ? parsedFromIso.format('MM/DD/YYYY')
      : undefined;
  }, []);

  const {
    startDate: rawStartDate,
    endDate: rawEndDate,
    setStartDate: setControlledStartDate,
    setEndDate: setControlledEndDate,
  } = useControlledDates({
    propStartDate,
    propEndDate,
    initialWidgetStartDate,
    initialWidgetEndDate,
    isWidget,
  });

  const startDate = useMemo(
    () => normalizeDisplayDate(rawStartDate) ?? initialWidgetStartDate,
    [normalizeDisplayDate, rawStartDate],
  );

  const endDate = useMemo(
    () => normalizeDisplayDate(rawEndDate) ?? initialWidgetEndDate,
    [normalizeDisplayDate, rawEndDate],
  );

  const startMoment = useMemo(() => {
    if (!startDate) return null;
    const parsed = dayjs(startDate, 'MM/DD/YYYY');
    return parsed.isValid() ? parsed.startOf('day') : null;
  }, [startDate]);

  const endMoment = useMemo(() => {
    if (!endDate) return null;
    const parsed = dayjs(endDate, 'MM/DD/YYYY');
    return parsed.isValid() ? parsed.endOf('day') : null;
  }, [endDate]);

  const [page, setPage] = useState(0);

  const onDateRangeChange = useCallback(
    (nextStart: string, nextEnd: string) => {
      if (!nextStart || !nextEnd) return;
      setControlledStartDate(nextStart);
      setControlledEndDate(nextEnd);
      setPage(0);
    },
    [setControlledEndDate, setControlledStartDate],
  );

  const parseForApi = useCallback((value?: string) => {
    if (!value) return '';
    const parsedFromDisplay = dayjs(value, 'MM/DD/YYYY');
    if (parsedFromDisplay.isValid()) {
      return parsedFromDisplay.format('M/D/YYYY');
    }
    const parsedFromIso = dayjs(value);
    return parsedFromIso.isValid() ? parsedFromIso.format('M/D/YYYY') : '';
  }, []);

  const startParam = useMemo(
    () => parseForApi(startDate),
    [parseForApi, startDate],
  );
  const endParam = useMemo(() => parseForApi(endDate), [parseForApi, endDate]);
  useEffect(() => {
    setPage(0);
  }, [startParam, endParam]);

  const skipQueries = !authLoaded || !startParam || !endParam;

  const recentOpportunitiesQuery = useGetRuleOpportunitiesQuery(
    {
      ruleIds: RECENT_INSIGHT_RULE_IDS,
      startDate: startParam,
      endDate: endParam,
    },
    { skip: skipQueries, refetchOnMountOrArgChange: true },
  );

  const anyFetching = recentOpportunitiesQuery.isFetching;
  const isLoading = skipQueries || recentOpportunitiesQuery.isLoading;

  useEffect(() => {
    if (!skipQueries && !anyFetching) {
      onDataLoaded?.();
    }
  }, [skipQueries, anyFetching, onDataLoaded, recentOpportunitiesQuery.data]);

  const isStatusAllowed = useCallback((item: OpportunityItem) => {
    if (EXCLUDED_STATUS_IDS.has(item.StatusId)) {
      return false;
    }
    const statusName = (item.StatusName || '').trim();
    return statusName.length > 0;
  }, []);

  const allRows = useMemo(() => {
    const rows: InsightRow[] = [];
    const records = recentOpportunitiesQuery.data?.Records || [];

    const parseRecordMoment = (value: unknown) => {
      if (!value) return null;
      if (
        typeof value !== 'string' &&
        typeof value !== 'number' &&
        !(value instanceof Date) &&
        !dayjs.isDayjs(value)
      ) {
        return null;
      }
      const dateText =
        value instanceof Date ? value.toISOString() : String(value).trim();
      if (!dateText) return null;
      const isoParsed = dayjs(dateText);
      if (isoParsed.isValid()) {
        return isoParsed;
      }
      const fourDigit = dayjs(dateText, 'M/D/YYYY');
      if (fourDigit.isValid()) {
        return fourDigit;
      }
      const shortParsed = dayjs(dateText, 'M/D/YY');
      if (shortParsed.isValid()) {
        return shortParsed;
      }
      return null;
    };

    records.forEach((record) => {
      if (!isStatusAllowed(record)) return;
      const ruleId = record.OppRuleId;
      if (ruleId == null || !RECENT_INSIGHT_RULE_IDS_SET.has(ruleId)) {
        return;
      }
      const recordMoment = parseRecordMoment(record.Date);

      if (startMoment && endMoment) {
        if (!recordMoment) {
          return;
        }
        if (
          recordMoment.isBefore(startMoment) ||
          recordMoment.isAfter(endMoment)
        ) {
          return;
        }
      }
      const investorRef = record.Investors?.[0];
      const recordDate =
        typeof record.Date === 'string' && record.Date.trim()
          ? record.Date
          : null;
      rows.push({
        id: `${String(ruleId)}-${String(record.OpportunityId)}`,
        clientName:
          investorRef?.InvestorName ||
          t('recentInsights.unknownClient', 'Client'),
        investorId:
          typeof investorRef?.InvestorId === 'number'
            ? investorRef.InvestorId
            : null,
        amount:
          typeof record.Amount === 'number' && Number.isFinite(record.Amount)
            ? Math.abs(record.Amount)
            : 0,
        date: recordDate,
        wealthAccountId: record.WealthAccountId ?? null,
        ruleId,
        item: record,
      });
    });

    return rows.sort((a, b) => {
      const momentA = parseRecordMoment(a.date) ?? dayjs(0);
      const momentB = parseRecordMoment(b.date) ?? dayjs(0);
      return momentB.valueOf() - momentA.valueOf();
    });
  }, [
    recentOpportunitiesQuery.data,
    isStatusAllowed,
    t,
    startMoment,
    endMoment,
  ]);

  const totalRows = allRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, totalPages - 1);
  const paginatedRows = allRows.slice(
    currentPage * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE,
  );

  useEffect(() => {
    if (page >= totalPages) {
      setPage(0);
    }
  }, [page, totalPages]);

  const labelMap = useMemo(
    () => ({
      [OpportunityRuleIdEnum.MoneyInMotionNewAccounts]: t(
        'recentInsights.moneyInMotionNewAccounts',
        'Money in Motion - New Accounts',
      ),
      [OpportunityRuleIdEnum.MoneyInMotionLargeDeposits]: t(
        'recentInsights.moneyInMotionLargeDeposits',
        'Money in Motion - Large Deposits',
      ),
      [OpportunityRuleIdEnum.Rollover]: t(
        'recentInsights.rolloverOpportunity',
        'Rollover Opportunity',
      ),
      [OpportunityRuleIdEnum.InvestableCash]: t(
        'recentInsights.investableCash',
        'Investable Cash',
      ),
    }),
    [t],
  );

  const [selectedAccount, setSelectedAccount] = useState<AccountBase | null>(
    null,
  );
  const [selectedAccountTab, setSelectedAccountTab] = useState<
    string | undefined
  >(undefined);
  const [selectedAccountInvestorId, setSelectedAccountInvestorId] = useState<
    number | undefined
  >(undefined);
  const [selectedClient, setSelectedClient] = useState<AdvisorInvestor | null>(
    null,
  );
  const [selectedClientTab, setSelectedClientTab] = useState<
    string | undefined
  >(undefined);
  const accountCacheRef = useRef(
    new Map<number, { account: AccountBase; investorId: number }>(),
  );
  const [fetchAccounts] = useLazyGetAccountsQuery();

  const handleAccountOpen = useCallback(
    async (
      wealthAccountId: number | null,
      investorId: number | null,
      tabId?: string,
    ) => {
      if (!wealthAccountId || !investorId) return;

      const cached = accountCacheRef.current.get(wealthAccountId);
      if (cached) {
        setSelectedAccountTab(tabId);
        setSelectedAccountInvestorId(cached.investorId);
        setSelectedAccount(cached.account);
        return;
      }

      try {
        const result = await fetchAccounts({
          showClosed: false,
          InvestorId: investorId,
        }).unwrap();
        const records = result.Records;
        records.forEach((account) => {
          accountCacheRef.current.set(account.AccountId, {
            account,
            investorId,
          });
        });
        const match = records.find((acc) => acc.AccountId === wealthAccountId);
        if (match) {
          setSelectedAccountTab(tabId);
          setSelectedAccountInvestorId(investorId);
          setSelectedAccount(match);
        }
      } catch (error) {
        console.error('Failed to load account details', error);
      }
    },
    [fetchAccounts],
  );

  // The embeddable widget bundle renders inside an isolated MemoryRouter
  // (see WidgetProviders) where navigate() is a no-op, so the drill-in has to
  // go through the standalone flyout there instead of the routed page.
  const handleClientOpen = useCallback(
    (investorId: number | null, clientName: string, tabKey?: string) => {
      if (!investorId) return;
      const stub: AdvisorInvestor = {
        CanMGPSSO: false,
        InvestorId: investorId,
        Name: clientName,
        NetWorth: 0,
        TotalAssets: 0,
        TotalLiabilities: 0,
        Home: 0,
        HomePercentage: 0,
        OnboardingStep: 0,
        OnboardingStepDisplay: '',
        CanInvite: false,
        Groups: [],
        CanImpersonate: false,
        CanDelete: false,
        InHousehold: false,
        UserId: 0,
        UserName: '',
        IsAdminLocked: false,
        IsSensitiveUser: false,
      };
      setSelectedClientTab(tabKey);
      setSelectedClient(stub);
    },
    [],
  );

  const handleRowClick = useCallback(
    (row: InsightRow) => {
      switch (row.ruleId) {
        case OpportunityRuleIdEnum.MoneyInMotionNewAccounts:
        case OpportunityRuleIdEnum.Rollover:
          void handleAccountOpen(row.wealthAccountId, row.investorId);
          break;
        case OpportunityRuleIdEnum.MoneyInMotionLargeDeposits:
          void handleAccountOpen(
            row.wealthAccountId,
            row.investorId,
            'transactions',
          );
          break;
        case OpportunityRuleIdEnum.InvestableCash:
          if (isEmbeddableWidgetBundle) {
            handleClientOpen(row.investorId, row.clientName, 'assetAllocation');
          } else if (row.investorId) {
            navigate(
              buildAdvisorClientDetailUrl(row.investorId, 'assetAllocation'),
            );
          }
          break;
        default:
          break;
      }
    },
    [handleAccountOpen, handleClientOpen, navigate],
  );

  const pageStart = currentPage * ITEMS_PER_PAGE + 1;
  const pageEnd = Math.min(
    (currentPage + 1) * ITEMS_PER_PAGE,
    totalRows || ITEMS_PER_PAGE,
  );

  return (
    <PaperFrameStyled
      ref={widgetWidthRef}
      sx={{ flex: 1 }}
    >
      <Stack
        spacing={0}
        sx={{ height: '100%' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            pt: 3,
          }}
        >
          <Typography
            variant='h6'
            sx={{ fontWeight: 600 }}
          >
            {widgetTitle}
          </Typography>
          {isWidget && (
            <Box sx={{ minWidth: isMobile ? '100%' : 280 }}>
              <DateRange
                startDate={startDate}
                endDate={endDate}
                onChange={onDateRangeChange}
                useWidgetNarrowIconField={widgetNarrowDateField}
              />
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ flex: 1 }}>
          {isLoading || anyFetching ? (
            <Stack
              spacing={2}
              sx={{ px: 3 }}
            >
              {Array.from({ length: ITEMS_PER_PAGE }).map((_, idx) => (
                <Box key={idx}>
                  <Skeleton
                    variant='rectangular'
                    height={OUTLINED_INPUT_HEIGHT}
                    sx={{ mb: 1 }}
                  />
                </Box>
              ))}
            </Stack>
          ) : paginatedRows.length === 0 ? (
            <Box sx={{ px: 3, py: 4 }}>
              <Typography color='text.secondary'>
                {t(
                  'recentInsights.emptyState',
                  'No insights match the selected filters.',
                )}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0}>
              {paginatedRows.map((row, index) => {
                const description = labelMap[row.ruleId];
                const amountDisplay = formatNumberToCurrency(row.amount);
                const dateDisplay = formatAppDate(row.date, {
                  format: 'M/D/YY',
                });
                const showDivider = index !== paginatedRows.length - 1;
                const isClientDetailEntry =
                  row.ruleId === OpportunityRuleIdEnum.InvestableCash;
                const hasInvestorId = Boolean(row.investorId);
                const hasAccountId = Boolean(row.wealthAccountId);
                const isInteractive = isClientDetailEntry
                  ? // The flyout drill-in (embeddable bundle only) doesn't
                    // depend on route permissions the way the routed page's
                    // navigate() does.
                    (isEmbeddableWidgetBundle || canNavigateToClients) &&
                    hasInvestorId
                  : hasInvestorId && hasAccountId;
                const rowContent = (
                  <>
                    <Stack
                      direction='row'
                      alignItems='center'
                      spacing={2}
                    >
                      <EmojiObjectsOutlinedIcon
                        color='action'
                        sx={{ fontSize: '2em' }}
                      />
                      <Box>
                        <Typography
                          variant='subtitle1'
                          sx={{ fontWeight: 600 }}
                          color='text.primary'
                        >
                          {row.clientName}
                        </Typography>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                        >
                          {description}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack
                      spacing={0.5}
                      alignItems='flex-end'
                    >
                      <Typography
                        variant='subtitle1'
                        sx={{ fontWeight: 600 }}
                        color='text.primary'
                      >
                        {amountDisplay}
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                      >
                        {dateDisplay}
                      </Typography>
                    </Stack>
                  </>
                );

                return (
                  <Box key={row.id}>
                    {isInteractive ? (
                      <Button
                        variant='text'
                        color='inherit'
                        fullWidth
                        onClick={() => {
                          handleRowClick(row);
                        }}
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          px: 3,
                          py: 2,
                          minWidth: 0,
                          borderRadius: 0,
                          textAlign: 'inherit',
                          textTransform: 'none',
                          transition: (theme) =>
                            theme.transitions.create('background-color', {
                              duration: theme.transitions.duration.shortest,
                            }),
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          '&.Mui-focusVisible': {
                            outline: (theme) =>
                              `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: -2,
                          },
                          '@media (prefers-reduced-motion: reduce)': {
                            transition: 'none',
                          },
                        }}
                      >
                        {rowContent}
                      </Button>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          px: 3,
                          py: 2,
                        }}
                      >
                        {rowContent}
                      </Box>
                    )}
                    {showDivider && <Divider />}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            gap: 2,
          }}
        >
          <Typography
            variant='caption'
            color='text.secondary'
          >
            {t('recentInsights.pagination', {
              defaultValue: '{{start}}-{{end}} of {{total}}',
              start: totalRows === 0 ? 0 : pageStart,
              end: totalRows === 0 ? 0 : pageEnd,
              total: totalRows,
            })}
          </Typography>
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
          >
            <IconButton
              size='small'
              onClick={() => {
                setPage((prev) => Math.max(0, prev - 1));
              }}
              disabled={currentPage === 0 || totalRows === 0}
            >
              <NavigateBeforeOutlinedIcon />
            </IconButton>
            <IconButton
              size='small'
              onClick={() => {
                setPage((prev) => Math.min(totalPages - 1, prev + 1));
              }}
              disabled={currentPage >= totalPages - 1 || totalRows === 0}
            >
              <NavigateNextOutlinedIcon />
            </IconButton>
          </Stack>
        </Box>

        <Divider />

        {/* TODO: Add view all insights button once the insights page is implemented */}
        {/* <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Button variant="text" onClick={handleViewAll} sx={{ fontWeight: 600 }}>
            {t("recentInsights.viewAllInsights", "View All Insights")}
          </Button>
        </Box> */}
      </Stack>

      {selectedAccount && (
        <AccountDetailsFlyout
          open={!!selectedAccount}
          toggleDrawer={(value) => {
            if (!value) {
              setSelectedAccount(null);
              setSelectedAccountTab(undefined);
              setSelectedAccountInvestorId(undefined);
            }
          }}
          account={selectedAccount}
          tabsPanelKey='advisor-account'
          initialTabId={selectedAccountTab}
          investorId={selectedAccountInvestorId}
        />
      )}
      {selectedClient && (
        <ClientDetailsFlyout
          open={!!selectedClient}
          toggleDrawer={(value) => {
            if (!value) {
              setSelectedClient(null);
              setSelectedClientTab(undefined);
            }
          }}
          client={selectedClient}
          initialTabKey={selectedClientTab}
        />
      )}
    </PaperFrameStyled>
  );
}
